/**
 * Root App component
 * Sets up theme, routing, and global providers
 */

import { useMemo } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { lightTheme, darkTheme } from './theme';
import { ThemeModeProvider, useThemeModeContext } from './contexts/ThemeModeContext';
import { DebugPanel } from './components/DebugPanel';
import AuthGate from './features/auth/AuthGate';
import AuthPage from './pages/AuthPage';
import DownloadPage from './pages/DownloadPage';
import HomePage from './pages/HomePage';
import { AgentsPage } from './pages/AgentsPage';
import { EnrollmentsPage } from './pages/EnrollmentsPage';
import { TerminalPage } from './pages/TerminalPage';
import { WebRTCPage } from './pages/WebRTCPage';
import { MetricsPage } from './pages/MetricsPage';
import { ConfigPage } from './pages/ConfigPage';
import { UpdatesPage } from './pages/UpdatesPage';
import ProfilePage from './pages/ProfilePage';
import ChatPage from './pages/ChatPage';
import ChatConversationPage from './pages/ChatConversationPage';
import MainLayout from './components/layout/MainLayout';
import PricingPage from './pages/PricingPage';

function InnerAppContent() {
  const { effectiveMode } = useThemeModeContext();

  // Memoize theme to avoid unnecessary re-renders
  const theme = useMemo(() => {
    return effectiveMode === 'dark' ? darkTheme : lightTheme;
  }, [effectiveMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <DebugPanel />
      <Routes>
        {/* Public routes — accessible without authentication.
            `/` is the default landing page (DownloadPage with public info + downloads).
            Anyone visiting the site lands on the marketing/download page; the
            admin panel is reached via the "Iniciar sesión" button there. */}
        <Route path="/" element={<DownloadPage />} />
        <Route path="/download" element={<DownloadPage />} />
        <Route path="/landing" element={<DownloadPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/pricing" element={<PricingPage />} />

        {/* Protected routes with MainLayout */}
        <Route
          path="/*"
          element={
            <AuthGate>
              <MainLayout>
                <Routes>
                  <Route path="/agents" element={<AgentsPage />} />
                  <Route path="/enrollments" element={<EnrollmentsPage />} />
                  <Route path="/terminal/:agentId" element={<TerminalPage />} />
                  <Route path="/session/:agentId" element={<WebRTCPage />} />
                  <Route path="/metrics/:agentId" element={<MetricsPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/config" element={<ConfigPage />} />
                  <Route path="/updates" element={<UpdatesPage />} />
                  <Route path="/chat" element={<ChatPage />} />
                  <Route path="/chat/:agentId" element={<ChatConversationPage />} />
                  {/* Admin dashboard reachable at /dashboard once authenticated */}
                  <Route path="/dashboard" element={<HomePage />} />
                  {/* Catch all within protected routes */}
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </MainLayout>
            </AuthGate>
          }
        />
      </Routes>
    </ThemeProvider>
  );
}

function AppContent() {
  return (
    <ThemeModeProvider>
      <InnerAppContent />
    </ThemeModeProvider>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
